"use client";

import { cva, type VariantProps } from "../../lib/cva";
import * as React from "react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Box } from "../../components/ui/box";
import { Icon, type GlyphName } from "../../components/ui/icon";
import type { Color } from "../../lib/colors";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

/**
 * Sized, stateful avatar parts for entity visuals — the size scale, the
 * suspended ("muted") greyscale, and the invited dashed-ring treatments are
 * one visual system shared by single avatars, avatar stacks (EntityGroup),
 * and count chips. Consumers supply content (initials, glyphs, images);
 * nothing here knows what an entity is.
 */

export type EntityAvatarSize = "2xs" | "xs" | "sm" | "default" | "lg" | "xl";

// Single source of truth for avatar diameter (px) per tier, mirroring the
// `size-N` classes in entityAvatarVariants. Consumers sizing a sibling box to
// match an avatar must read this rather than re-encoding the literals.
export const AVATAR_SIZE_PX: Record<EntityAvatarSize, number> = {
  "2xs": 16,
  xs: 20,
  sm: 24,
  default: 32,
  lg: 48,
  xl: 64,
};

export const entityAvatarVariants = cva("relative flex shrink-0 select-none", {
  variants: {
    size: {
      "2xs": "size-4",
      xs: "size-5",
      sm: "size-6",
      default: "size-8",
      lg: "size-12",
      xl: "size-16",
    },
    shape: {
      circle: "rounded-full",
      square: "rounded-md",
    },
    invited: {
      true: "border border-dashed border-muted-foreground/60",
    },
  },
  // A square tile's radius tracks its size: `rounded-md` (8px) on a 16–24px
  // tile rounds away the corners entirely and the square reads as a circle —
  // which erases the tile-vs-circle contrast that IS the square shape's job.
  compoundVariants: [
    { shape: "square", size: "2xs", class: "rounded-xs" },
    { shape: "square", size: "xs", class: "rounded-xs" },
    { shape: "square", size: "sm", class: "rounded-sm" },
    { shape: "square", size: "xl", class: "rounded-lg" },
  ],
  defaultVariants: {
    size: "default",
    shape: "circle",
  },
});

// How many initials fit a circle of each size. The 16px rung — toolbar
// glyph height, crumbs — has room for one letter only; two 10px letters
// there read as a smudge. From 20px up the circle carries first + last.
export const AVATAR_INITIALS_MAX: Record<EntityAvatarSize, 1 | 2> = {
  "2xs": 1,
  xs: 2,
  sm: 2,
  default: 2,
  lg: 2,
  xl: 2,
};

// Glyph box for the no-identity icon fallback per avatar rung — roughly the
// share of the diameter initials occupy. Literal size-* classes so Tailwind
// emits them. The silhouette rungs run a step larger than the stroked-glyph
// share: a solid shape holds its silhouette at that density where a stroked
// one would smear. No xl entry: a bare box class keeps Icon's default md
// stroke (1.75), and scaled to a half-diameter box on the 64px hero plate
// that renders ~2.3px — the glyph shouts. That rung passes `size="xl"`
// instead, the reviewed display pairing (24px box, 1.5 stroke).
const FALLBACK_ICON_CLASS: Record<Exclude<EntityAvatarSize, "xl">, string> = {
  "2xs": "size-[11px]",
  xs: "size-[13px]",
  sm: "size-3.5",
  default: "size-4",
  lg: "size-6",
};

// The no-identity glyph per rung. Below the 24px rung, registry artwork
// fails — a 1.5–2.0 source stroke lands sub-pixel on a 10–13px box and the
// glyph reads as a smudge — so those rungs render a purpose-built solid
// silhouette instead: avatar furniture like the checkbox face, not a product
// glyph, so it lives here rather than in the icon registry (which is
// outline-only by policy). The xl rung takes the display pairing (see
// FALLBACK_ICON_CLASS); everything between renders the passed kind glyph on
// the compact share.
function fallbackGlyph(
  rung: EntityAvatarSize,
  icon: GlyphName,
): React.ReactElement {
  if (rung === "2xs" || rung === "xs") {
    return <FallbackSilhouette className={FALLBACK_ICON_CLASS[rung]} />;
  }
  if (rung === "xl") return <Icon name={icon} size="xl" />;
  return <Icon className={FALLBACK_ICON_CLASS[rung]} name={icon} />;
}

// One shared person silhouette for every kind at the small rungs: a 16–20px
// circle has no room for kind nuance (a card frame around a 11px person says
// nothing), so kinds converge and the surrounding label carries the kind.
function FallbackSilhouette({
  className,
}: {
  className: string;
}): React.ReactElement {
  return (
    <svg
      aria-hidden="true"
      className={cn("shrink-0", className)}
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <circle cx={12} cy={7.6} r={4.4} />
      <path d="M12 13.6c-4.9 0-8.2 2.8-8.2 6.4h16.4c0-3.6-3.3-6.4-8.2-6.4z" />
    </svg>
  );
}

/**
 * Initials for an avatar fallback. Pass the avatar's RESOLVED size so the
 * count follows the circle it renders in (`AVATAR_INITIALS_MAX`); without a
 * size the full two-initial form is returned. Null when the name yields no
 * initials — render a glyph (`EntityAvatarFallback`'s `icon`) there, never
 * punctuation.
 */
export function getInitials(
  name: string | null | undefined,
  size?: EntityAvatarSize,
): string | null {
  if (!name) return null;
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0);
  const first = parts[0]?.[0];
  if (!first) return null;
  if (size && AVATAR_INITIALS_MAX[size] === 1) return first.toUpperCase();
  // First and LAST, capped at two — a person's initials, which is what
  // distinguishes two teammates who share a first name. Middle names are
  // dropped rather than crowding the circle. A one-word name stays one
  // character, so this never invents an initial nobody has.
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : undefined;
  return (first + (last ?? "")).toUpperCase();
}

/** Live presence states an indicator can show. */
export type EntityAvatarPresenceStatus = "online" | "away" | "offline";

const PRESENCE_LABELS: Record<EntityAvatarPresenceStatus, string> = {
  online: "Online",
  away: "Away",
  offline: "Offline",
};

export const entityAvatarPresenceDotVariants = cva(
  "pointer-events-none absolute z-10 rounded-full border-background",
  {
    variants: {
      status: {
        online: "bg-success",
        away: "bg-warning",
        // Solid (no alpha): the dot overlaps the avatar's edge, so a
        // translucent fill would show the circle through it and shift hue
        // per avatar color. muted-foreground is the neutral grey ramp.
        offline: "bg-muted-foreground",
      },
      // Dot diameter per avatar size — big enough to read at a glance,
      // small enough not to swallow the circle. Ring thickness scales with
      // it (1px under 24px avatars, 2px from `default` up).
      //
      // Offsets put the dot's CENTER on the avatar circle's edge at the
      // bottom-right diagonal — half over the avatar, half off. Per pairing:
      // offset = r(1−1/√2) − d/2, rounded to the px grid, so small avatars
      // push out ~1px while lg/xl pull in — the corner of their bounding box
      // sits further outside the circle.
      size: {
        "2xs": "size-1.5 border -bottom-px -right-px",
        xs: "size-2 border -bottom-px -right-px",
        sm: "size-2 border -bottom-px -right-px",
        default: "size-2.5 border-2 -bottom-px -right-px",
        lg: "size-3 border-2 bottom-0 right-0",
        xl: "size-3.5 border-2 bottom-0.5 right-0.5",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

// Mobile variant — a SOLID phone silhouette in the status color: a filled
// rounded-rect body with a background-colored "screen" cutout, ringed in the
// page background for the same knockout the dot gets. Anchored on the dot's
// own per-size offsets — the silhouette is compact enough to share them.
// Portrait proportions per avatar size; arbitrary px classes are literal so
// Tailwind emits them.
export const entityAvatarPresenceMobileVariants = cva(
  "pointer-events-none absolute z-10 border-background",
  {
    variants: {
      status: {
        online: "bg-success",
        away: "bg-warning",
        offline: "bg-muted-foreground",
      },
      size: {
        "2xs": "w-[6px] h-[9px] rounded-[2px] border -bottom-px -right-px",
        xs: "w-[7px] h-[10px] rounded-[2px] border -bottom-px -right-px",
        sm: "w-[7px] h-[11px] rounded-[2px] border -bottom-px -right-px",
        default: "w-[9px] h-[13px] rounded-[3px] border -bottom-px -right-px",
        lg: "w-[10px] h-[15px] rounded-[3px] border-2 bottom-0 right-0",
        xl: "w-[12px] h-[17px] rounded-[3px] border-2 bottom-0.5 right-0.5",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

// The screen cutout, inset from the body so the status color reads as
// bezels: 1px sides, thicker chin at the bottom (the phone read). Keyed by
// the same avatar size as the body.
export const entityAvatarPresenceMobileScreenVariants = cva(
  "absolute bg-background",
  {
    variants: {
      size: {
        "2xs": "left-px right-px top-px bottom-[2px] rounded-[1px]",
        xs: "left-px right-px top-px bottom-[2px] rounded-[1px]",
        sm: "left-px right-px top-[2px] bottom-[2px] rounded-[1px]",
        default: "left-px right-px top-[2px] bottom-[3px] rounded-[1px]",
        lg: "left-px right-px top-[2px] bottom-[3px] rounded-[1px]",
        xl: "left-[2px] right-[2px] top-[2px] bottom-[3px] rounded-[1px]",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

export type EntityAvatarPresence = Omittable<{
  status: EntityAvatarPresenceStatus;
  /** Presence comes from a phone: render a smartphone glyph in the status
   *  color instead of the dot. */
  mobile?: boolean;
}>;

/**
 * Live-presence indicator overlapping the avatar's bottom-right corner —
 * green online, yellow away, grey offline. Web/desktop presence renders as a
 * dot ringed in the page background; phone-only presence (`mobile`) renders
 * a smartphone glyph in the same status color.
 *
 * Prefer `EntityAvatar`'s `presence` prop, which composes this for you (the
 * avatar root clips to its circle, so the prop renders the indicator as a
 * sibling inside a relative wrapper). Purely decorative to pointers; the
 * status is exposed to assistive tech as a label.
 */
export function EntityAvatarPresenceIndicator({
  status,
  size,
  mobile,
}: EntityAvatarPresence & { size?: EntityAvatarSize }): React.ReactElement {
  if (mobile) {
    return (
      <Box
        aria-label={`${PRESENCE_LABELS[status]} on mobile`}
        as="span"
        className={entityAvatarPresenceMobileVariants({ status, size })}
        role="img"
      >
        <Box
          as="span"
          className={entityAvatarPresenceMobileScreenVariants({ size })}
        />
      </Box>
    );
  }
  return (
    <Box
      aria-label={PRESENCE_LABELS[status]}
      as="span"
      className={entityAvatarPresenceDotVariants({ status, size })}
      role="img"
    />
  );
}

type EntityAvatarState = {
  muted?: boolean;
  invited?: boolean;
  size?: EntityAvatarSize | null | undefined;
};
const EntityAvatarStateContext = React.createContext<EntityAvatarState>({});

export type EntityAvatarProps = React.ComponentProps<typeof Avatar> &
  VariantProps<typeof entityAvatarVariants> &
  Omittable<{
    /** Suspended treatment — greyscale + dimmed image, gray fallback. */
    muted?: boolean;
    /** Invited treatment — dashed ring + reduced opacity. */
    invited?: boolean;
    /** Accessible label for the avatar. */
    alt?: string;
    /** Live presence indicator on the avatar's bottom-right corner. The
     *  avatar root clips to its circle, so the indicator renders as a
     *  sibling inside a relative wrapper rather than as a child. */
    presence?: EntityAvatarPresence | null;
  }>;

export function EntityAvatar({
  className,
  size,
  shape,
  muted,
  invited,
  alt,
  presence,
  children,
  ...props
}: EntityAvatarProps): React.ReactElement {
  const state = React.useMemo(
    () => ({ muted: !!muted, invited: !!invited, size }),
    [muted, invited, size],
  );
  const avatar = (
    <EntityAvatarStateContext.Provider value={state}>
      <Avatar
        aria-label={alt}
        className={cn(
          entityAvatarVariants({ size, shape, invited }),
          className,
        )}
        data-slot="entity-avatar"
        {...props}
      >
        {children}
      </Avatar>
    </EntityAvatarStateContext.Provider>
  );
  // Suspended/invited avatars suppress the dot — "online" is meaningless for
  // an account that cannot be signed in.
  if (!presence || muted || invited) return avatar;
  return (
    <Box as="span" className="relative inline-flex shrink-0">
      {avatar}
      <EntityAvatarPresenceIndicator
        mobile={presence.mobile ?? false}
        size={size ?? "default"}
        status={presence.status}
      />
    </Box>
  );
}

export function EntityAvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarImage>): React.ReactElement {
  const { muted, invited } = React.useContext(EntityAvatarStateContext);
  return (
    <AvatarImage
      className={cn(
        "aspect-square",
        muted && "grayscale brightness-[0.25]",
        invited && "grayscale opacity-40",
        className,
      )}
      {...props}
    />
  );
}

export function EntityAvatarFallback({
  className,
  color,
  gradient,
  icon,
  children,
  ...props
}: React.ComponentProps<typeof AvatarFallback> &
  Omittable<{
    /** Solid key-color fill with the color's contrast ink — the initials
     *  circle painted in a chosen color instead of the brand default. The
     *  muted/invited gray states still win over it. */
    color?: Color | null;
    /** Subtle diagonal sheen over the fill — a touch lighter toward the
     *  top-left, a touch darker toward the bottom-right — for identity tiles
     *  that carry a hint of depth. The gray states stay flat. */
    gradient?: boolean;
    /** No-identity fallback: this glyph, muted on the neutral wash, when the
     *  entity has no text to take initials from (`getInitials` returned
     *  null). Beats `color` — a key-color fill around a generic glyph would
     *  fake an identity nobody set. Ignored when children render. Below the
     *  `sm` rung the name only marks "render the fallback" — the solid
     *  silhouette draws in its place (see `fallbackGlyph`). */
    icon?: GlyphName | null;
  }>): React.ReactElement {
  const { muted, invited, size } = React.useContext(EntityAvatarStateContext);
  const gray = muted || invited;
  const glyph = children == null && icon != null;
  return (
    <AvatarFallback
      className={cn(
        // Follow the root's radius — circle, square, or a consumer override
        // (e.g. a tighter square at very small sizes) — instead of restating
        // a shape of its own that can drift from the clip.
        "rounded-[inherit]",
        // Text color goes on the element; descendant text inherits it.
        gray || glyph
          ? "bg-muted text-muted-foreground"
          : color
            ? "bg-c text-c-foreground"
            : "bg-primary text-primary-foreground",
        // A gradient is background-image, so it composites over whichever
        // fill the tile resolved to above.
        !gray && !glyph && gradient && "bg-linear-to-br from-white/15 to-black/10",
        invited && "opacity-40",
        className,
      )}
      data-color={!gray && !glyph && color ? color : undefined}
      {...props}
    >
      {glyph ? fallbackGlyph(size ?? "default", icon) : children}
    </AvatarFallback>
  );
}
